import { slugify } from '@/utils/slugify';

interface ServerPromotion {
  promotion_id: string;
  promotion_name: string;
  creative_slot?: string;
}

// ONE message, no `{ ecommerce: null }` reset — same message shape as
// useDataLayer's pushEvent. See the comment there for why the reset is gone.
// Routes through the consent queue so ecommerce events land after GA4 Config.
const pushEvent = (event: string, ecommerce: Record<string, unknown>) => {
  const obj = { event, ecommerce };
  if (window.pushEcommerce) {
    window.pushEcommerce(obj);
  } else if (window.dataLayer) {
    window.dataLayer.push(obj);
  }
};

const parsePromotion = (el: Element): ServerPromotion | null => {
  const name = el.getAttribute('data-ga-promotion');
  if (!name) {
    return null;
  }
  return {
    promotion_id: slugify(name),
    promotion_name: name,
    creative_slot: el.getAttribute('data-ga-creative-slot') ?? undefined,
  };
};

export const trackServerBlocks = () => {
  const promoElements = document.querySelectorAll('[data-ga-promotion]');

  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          observer.unobserve(entry.target);
          const promotion = parsePromotion(entry.target);
          if (promotion) {
            pushEvent('view_promotion', { items: [promotion] });
          }
        }
      },
      { threshold: 0.5 },
    );
    promoElements.forEach(el => observer.observe(el));
  } else {
    // No observer support: fire immediately rather than drop the impression.
    promoElements.forEach(el => {
      const promotion = parsePromotion(el);
      if (promotion) {
        pushEvent('view_promotion', { items: [promotion] });
      }
    });
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }

    const nav = target.closest('[data-ga-nav]');
    if (nav) {
      pushEvent('navigation_click', {
        nav_type: nav.getAttribute('data-ga-nav'),
        link_text: (nav.textContent ?? '').trim(),
        link_url: nav.getAttribute('href') ?? '',
      });
      return;
    }

    const host = target.closest('[data-ga-promotion]');
    if (!host) {
      return;
    }
    // Anywhere inside the block is an impression surface, but only a CTA (or
    // the block itself being one big link) counts as a promotion click.
    const isCta =
      target.closest('[data-ga-promotion-cta]') !== null ||
      host instanceof HTMLAnchorElement;
    if (!isCta) {
      return;
    }
    const promotion = parsePromotion(host);
    if (promotion) {
      pushEvent('select_promotion', { items: [promotion] });
    }
  });
};
